import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { form, FormField, validate } from '@angular/forms/signals';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonItemGroup,
  IonList,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular';
import { ViewModelStore } from '../../../lib/store.model';
import type { KitOverlayController } from '@rdlabo/ionic-angular-kit';
import { KitIonicFormField } from '@rdlabo/ionic-angular-kit/forms';

@Component({
  selector: 'app-password-prompt',
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-title>{{ heading() }}</ion-title>
        <ion-buttons slot="start">
          <ion-button type="button" (click)="vm.disableHandler($event, vm.cancel())">キャンセル</ion-button>
        </ion-buttons>
        <ion-buttons slot="end">
          <ion-button type="submit" [form]="formRef" [disabled]="passwordForm().invalid() || isClosing()"
            >変更</ion-button
          >
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <form #formRef novalidate (submit)="vm.disableHandler($event, vm.confirm())">
        @if (message()) {
          <p class="ion-padding-horizontal">{{ message() }}</p>
        }
        <ion-list
          ><ion-item-group>
            <ion-item>
              <ion-input
                label="現在のパスワード"
                labelPlacement="stacked"
                type="password"
                autocomplete="current-password"
                [formField]="passwordForm.password"
              ></ion-input>
            </ion-item> </ion-item-group
        ></ion-list>
      </form>
    </ion-content>
  `,
  imports: [
    FormField,
    KitIonicFormField,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonInput,
    IonItem,
    IonItemGroup,
    IonList,
    IonTitle,
    IonToolbar,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordPromptComponent {
  readonly vm = new ViewModel(this);
  declare static readonly modalReturn: string;
  readonly heading = input.required<string>();
  readonly message = input<string>();
  readonly retryError = input<string>();
  readonly password = signal({ password: '' });
  readonly passwordForm = form(this.password, (path) => {
    validate(path.password, ({ value }) =>
      value()
        ? undefined
        : {
            kind: 'required',
            message: this.retryError() || '現在のパスワードを入力してください',
          },
    );
  });
  readonly isClosing = signal(false);

  constructor() {
    effect(() => {
      if (this.retryError()) {
        this.passwordForm.password().markAsTouched();
      }
    });
  }
}

class ViewModel extends ViewModelStore<PasswordPromptComponent, 'passwordForm'> {
  readonly #modal = inject(ModalController);

  async confirm() {
    if (this.host.passwordForm().invalid() || this.host.isClosing()) {
      return;
    }
    this.host.isClosing.set(true);
    await this.#modal.dismiss(this.host.password().password, 'confirm').finally(() => this.host.isClosing.set(false));
  }

  cancel() {
    return this.#modal.dismiss(undefined, 'cancel');
  }
}

export const launchPasswordPrompt = (
  overlay: KitOverlayController,
  props: { heading: string; message?: string; retryError?: string },
): Promise<string | undefined> => overlay.presentModal(PasswordPromptComponent, props, { cssClass: 'half-modal' });
